import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useUiState } from "@/lib/uiState";
import SortAlphaBar, { sortFaculty, letterOfFaculty } from "@/components/SortAlphaBar";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  GraduationCap, Loader2, Plus, Pencil, Trash2, Search, Upload, Mail, MailX,
  Users2, CalendarRange, Send, X, Link2, Globe, Linkedin, BookMarked, UserSquare2, Sparkles,
  Eye, EyeOff, LayoutGrid, List, Star, ArrowUp, ArrowDown,
} from "lucide-react";

const SortArrow = ({ dir }) => (dir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />);

const STATUS_STYLES = {
  current: "bg-emerald-100 text-emerald-800",
  retired: "bg-sky-100 text-sky-800",
  emeritus: "bg-amber-100 text-amber-800",
  deceased: "bg-slate-200 text-slate-700",
};
const STATUS_LABELS = { current: "Current", retired: "Retired", emeritus: "Emeritus", deceased: "Deceased" };

const tenure = (s) => {
  if (s.year_start == null && s.year_end == null) return "—";
  const a = s.year_start ?? "?";
  const b = s.year_end == null ? "present" : s.year_end;
  return `${a}–${b}`;
};

const Initials = ({ name }) => <span className="text-sm font-semibold text-primary">{(name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}</span>;

/* ---------------- Detail modal ---------------- */
function StaffDetail({ s, onClose, onEdit }) {
  const links = [
    s.alumnus_id && { to: `/alumni/${s.alumnus_id}`, label: s.alumnus_name || "Alumni page", icon: UserSquare2, internal: true, testid: `detail-link-alumni` },
    s.wikipedia && { href: s.wikipedia, label: "Wikipedia", icon: BookMarked, testid: "detail-link-wiki" },
    s.linkedin && { href: s.linkedin, label: "LinkedIn", icon: Linkedin, testid: "detail-link-linkedin" },
    s.website && { href: s.website, label: "Website", icon: Globe, testid: "detail-link-web" },
  ].filter(Boolean);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="staff-detail-modal">
        <DialogHeader className="sr-only"><DialogTitle>{s.name}</DialogTitle><DialogDescription>Faculty / staff detail</DialogDescription></DialogHeader>
        <div className="flex items-start gap-4">
          <div className="h-20 w-20 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden border border-border shrink-0">
            {s.photo ? <img src={s.photo} alt={s.name} className="h-full w-full object-cover" /> : <span className="text-2xl font-semibold text-primary">{(s.name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif text-2xl leading-tight" data-testid="detail-name">{s.name}</h2>
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${STATUS_STYLES[s.status] || ""}`}>{STATUS_LABELS[s.status] || s.status}</span>
            </div>
            {s.title && <div className="text-sm text-foreground/90 mt-0.5">{s.title}</div>}
            {s.department && <div className="text-sm text-muted-foreground">{s.department}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-muted/40 rounded-md p-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CalendarRange className="h-3.5 w-3.5" /> Years served</div>
            <div className="text-sm mt-1" data-testid="detail-tenure">{tenure(s)}</div>
          </div>
          <div className="bg-muted/40 rounded-md p-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</div>
            <div className="text-sm mt-1 truncate">{s.email ? <a href={`mailto:${s.email}`} className="text-primary hover:underline">{s.email}</a> : <span className="text-muted-foreground">None on file</span>}</div>
          </div>
        </div>

        {s.bio && <div className="mt-4"><div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1">About</div><p className="text-sm text-foreground/85 whitespace-pre-line" data-testid="detail-bio">{s.bio}</p></div>}

        {links.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Links</div>
            <div className="flex flex-wrap gap-2">
              {links.map((l) => {
                const Icon = l.icon;
                const cls = `inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted ${l.internal ? "bg-accent/20 text-primary border-transparent hover:bg-accent/30" : ""}`;
                return l.internal
                  ? <Link key={l.testid} to={l.to} data-testid={l.testid} className={cls}><Icon className="h-4 w-4" /> {l.label}</Link>
                  : <a key={l.testid} href={l.href} target="_blank" rel="noreferrer" data-testid={l.testid} className={cls}><Icon className="h-4 w-4" /> {l.label}</a>;
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-border">
          <button data-testid="detail-close" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Close</button>
          <button data-testid="detail-edit" onClick={() => onEdit(s)} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"><Pencil className="h-4 w-4" /> Edit</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Add / edit modal ---------------- */
function StaffForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState(initial || { name: "", title: "", department: "", year_start: "", year_end: "", photo: "", email: "", bio: "", status: "current", wikipedia: "", linkedin: "", website: "", alumnus_id: "", alumnus_name: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aq, setAq] = useState("");
  const [aResults, setAResults] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "w-full px-3 py-2 rounded-md border border-border bg-background text-sm";

  const searchAlumni = async (q) => {
    setAq(q);
    if (q.trim().length < 2) { setAResults(null); return; }
    try { const r = await api.get(`/experience/search?q=${encodeURIComponent(q)}`); setAResults(r.data.members || []); }
    catch { setAResults([]); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", fileUrl(r.data.path || r.data.fileUrl || r.data.file_url));
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      ...f,
      year_start: f.year_start === "" ? null : parseInt(f.year_start, 10),
      year_end: f.year_end === "" ? null : parseInt(f.year_end, 10),
    };
    try {
      const r = initial ? await api.put(`/staff/${initial.id}`, payload) : await api.post("/staff", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved(r.data);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const aiLookup = async () => {
    if (!f.name.trim() || f.name.trim().length < 3) { toast.error("Enter the person's full name first"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/staff/ai-lookup", { name: f.name, title: f.title, department: f.department });
      const d = r.data || {};
      if (d.error) { toast.error(d.error); return; }
      setF((p) => ({
        ...p,
        title: p.title || d.title || "",
        department: p.department || d.department || "",
        year_start: (p.year_start ?? "") !== "" ? p.year_start : (d.year_start ?? ""),
        year_end: (p.year_end ?? "") !== "" ? p.year_end : (d.year_end ?? ""),
        status: p.status && p.status !== "current" ? p.status : (d.status || p.status || "current"),
        bio: p.bio || d.bio || "",
        wikipedia: p.wikipedia || d.wikipedia || "",
        linkedin: p.linkedin || d.linkedin || "",
        website: p.website || d.website || "",
        photo: p.photo || d.photo_url || "",
      }));
      const filled = ["title", "department", "bio", "wikipedia", "linkedin", "website", "photo_url"].filter((k) => d[k]).length + (d.year_start ? 1 : 0);
      toast.success(filled ? `AI filled ${filled} field${filled === 1 ? "" : "s"} — review before saving` : "No public details found — please fill in manually");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "AI lookup failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="staff-form-modal">
        <DialogHeader><DialogTitle className="font-serif">{initial ? "Edit" : "Add"} faculty / staff member</DialogTitle><DialogDescription>Record who they were and the years they served — or let AI look them up from their name.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden border border-border shrink-0">
              {f.photo ? <img src={f.photo} alt="" className="h-full w-full object-cover" /> : <Initials name={f.name} />}
            </div>
            <label data-testid="staff-photo-upload" className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-muted cursor-pointer">
              <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : (f.photo ? "Replace photo" : "Upload photo")}
              <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
            {f.photo && <button onClick={() => set("photo", "")} className="text-xs text-destructive hover:underline">Remove</button>}
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Name *</label>
            <div className="flex gap-2">
              <input data-testid="staff-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Dr. Jane Smith" />
              <button type="button" data-testid="staff-ai-lookup" onClick={aiLookup} disabled={aiBusy} title="Look up this person with AI" className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 disabled:opacity-60 shrink-0 whitespace-nowrap">
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {aiBusy ? "Looking up…" : "AI autofill"}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Enter a name, then let AI find their title, years, bio, photo and public links. Always review before saving.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Title / Role</label><input data-testid="staff-title" className={inp} value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Professor of English" /></div>
            <div><label className="text-xs text-muted-foreground">Department</label><input data-testid="staff-department" className={inp} value={f.department} onChange={(e) => set("department", e.target.value)} /></div>
            <div><label className="text-xs text-muted-foreground">Year started</label><input data-testid="staff-year-start" type="number" className={inp} value={f.year_start ?? ""} onChange={(e) => set("year_start", e.target.value)} placeholder="e.g. 1978" /></div>
            <div><label className="text-xs text-muted-foreground">Year ended <span className="text-muted-foreground/70">(blank = still here)</span></label><input data-testid="staff-year-end" type="number" className={inp} value={f.year_end ?? ""} onChange={(e) => set("year_end", e.target.value)} placeholder="e.g. 2004" /></div>
            <div><label className="text-xs text-muted-foreground">Email</label><input data-testid="staff-email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="name@school.edu" /></div>
            <div><label className="text-xs text-muted-foreground">Status</label>
              <select data-testid="staff-status" className={inp} value={f.status} onChange={(e) => set("status", e.target.value)}>
                {Object.keys(STATUS_LABELS).map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-xs text-muted-foreground">Short bio</label><textarea data-testid="staff-bio" rows={3} className={inp} value={f.bio} onChange={(e) => set("bio", e.target.value)} /></div>

          <div className="pt-2 border-t border-border">
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Links (optional)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Wikipedia</label><input data-testid="staff-wikipedia" className={inp} value={f.wikipedia} onChange={(e) => set("wikipedia", e.target.value)} placeholder="en.wikipedia.org/wiki/…" /></div>
              <div><label className="text-xs text-muted-foreground">LinkedIn</label><input data-testid="staff-linkedin" className={inp} value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="linkedin.com/in/…" /></div>
              <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">Website / social</label><input data-testid="staff-website" className={inp} value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-muted-foreground">Link to their alumni record <span className="text-muted-foreground/70">(if they're also an alum)</span></label>
              {f.alumnus_id ? (
                <div className="flex items-center gap-2 mt-1">
                  <span data-testid="staff-alumnus-linked" className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full bg-accent/20 text-primary"><Link2 className="h-3.5 w-3.5" /> {f.alumnus_name || "Alumni record linked"}</span>
                  <button type="button" onClick={() => { set("alumnus_id", ""); set("alumnus_name", ""); }} className="text-xs text-destructive hover:underline">Remove</button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <input data-testid="staff-alumnus-search" className={inp} value={aq} onChange={(e) => searchAlumni(e.target.value)} placeholder="Search alumni by name…" />
                  {aResults && aResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full max-h-44 overflow-y-auto bg-card border border-border rounded-md shadow-lg">
                      {aResults.slice(0, 8).map((m) => (
                        <button type="button" key={m.id} data-testid={`staff-alumnus-opt-${m.id}`} onClick={() => { set("alumnus_id", m.id); set("alumnus_name", m.name); setAq(""); setAResults(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted">
                          {m.name}{m.class_year ? <span className="text-muted-foreground text-xs"> '{String(m.class_year).slice(-2)}</span> : null}
                        </button>
                      ))}
                    </div>
                  )}
                  {aResults && aResults.length === 0 && aq.trim().length >= 2 && <div className="text-[11px] text-muted-foreground mt-1">No matches</div>}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="staff-submit" onClick={submit} disabled={saving} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{saving ? "Saving…" : (initial ? "Save changes" : "Add member")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Invite modal ---------------- */
function InviteModal({ allStaff, events, presetEventId, onClose }) {
  const [eventId, setEventId] = useState(presetEventId || "");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("We would be honored to have you join us. It would mean a great deal to the class to reconnect with you.");
  const [sending, setSending] = useState(false);

  const overlaps = (s, a, b) => {
    const lo = s.year_start == null ? -9999 : s.year_start;
    const hi = s.year_end == null ? 9999 : s.year_end;
    return lo <= b && hi >= a;
  };

  const autoSelect = () => {
    const a = parseInt(yearFrom, 10); const b = yearTo === "" ? a : parseInt(yearTo, 10);
    if (isNaN(a)) { toast.error("Enter a class year (e.g. 1985)"); return; }
    const eligible = allStaff.filter((s) => s.status !== "deceased" && overlaps(s, a, Math.max(a, b)));
    const ids = eligible.map((s) => s.id);
    setSelected(new Set(ids));
    const deceased = allStaff.filter((s) => s.status === "deceased" && overlaps(s, a, Math.max(a, b))).length;
    toast.success(`Selected ${ids.length} who served during ${a}${b && b !== a ? `–${b}` : ""}${deceased ? ` · ${deceased} deceased skipped` : ""}`);
  };

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const send = async () => {
    if (selected.size === 0) { toast.error("Select at least one person"); return; }
    if (!eventId && !subject.trim()) { toast.error("Pick an event or add a subject"); return; }
    setSending(true);
    try {
      const r = await api.post("/staff/invite", { staff_ids: [...selected], event_id: eventId, subject, message });
      const skipped = r.data.skipped?.length ? ` (${r.data.skipped.length} skipped — no email)` : "";
      toast.success(`Invitations sent to ${r.data.sent}${skipped}`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not send invitations"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-invite-modal">
        <DialogHeader><DialogTitle className="font-serif">Invite faculty &amp; staff to a function</DialogTitle><DialogDescription>Auto-select everyone who was here during a class's years, then send an invitation — tied to an event or as a standalone note.</DialogDescription></DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Event (optional)</label>
            <select data-testid="invite-event" className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm mt-1" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">No event — send a custom note</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}{ev.start ? ` · ${new Date(ev.start).toLocaleDateString()}` : ""}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">When an event is chosen, its date, place and details are included automatically.</p>
          </div>

          <div className="bg-muted/40 border border-border rounded-md p-3">
            <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CalendarRange className="h-3.5 w-3.5" /> Auto-select by class years</label>
            <div className="flex items-end gap-2 mt-2 flex-wrap">
              <div><span className="text-[11px] text-muted-foreground">From</span><input data-testid="invite-year-from" type="number" className="w-28 px-3 py-2 rounded-md border border-border bg-background text-sm block" value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} placeholder="1985" /></div>
              <div><span className="text-[11px] text-muted-foreground">To (optional)</span><input data-testid="invite-year-to" type="number" className="w-28 px-3 py-2 rounded-md border border-border bg-background text-sm block" value={yearTo} onChange={(e) => setYearTo(e.target.value)} placeholder="1989" /></div>
              <button data-testid="invite-autoselect" onClick={autoSelect} className="px-3 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 flex items-center gap-1.5"><Users2 className="h-4 w-4" /> Select who was here</button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Recipients ({selected.size} selected)</label>
              {selected.size > 0 && <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-destructive">Clear</button>}
            </div>
            <div className="border border-border rounded-md max-h-52 overflow-y-auto divide-y divide-border" data-testid="invite-recipient-list">
              {allStaff.map((s) => {
                const noEmail = !s.email;
                const dec = s.status === "deceased";
                return (
                  <label key={s.id} className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 ${noEmail || dec ? "opacity-60" : ""}`}>
                    <input type="checkbox" data-testid={`invite-check-${s.id}`} checked={selected.has(s.id)} onChange={() => toggle(s.id)} className="h-4 w-4 accent-[var(--primary)]" />
                    <span className="flex-1 min-w-0 truncate">{s.name} <span className="text-muted-foreground text-xs">· {s.title || "—"} · {tenure(s)}</span></span>
                    {dec ? <span className="text-[11px] text-muted-foreground">deceased</span> : noEmail ? <span className="text-[11px] text-muted-foreground flex items-center gap-1"><MailX className="h-3 w-3" /> no email</span> : <Mail className="h-3.5 w-3.5 text-muted-foreground" />}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-1 gap-3">
            <div><label className="text-xs text-muted-foreground">Subject {eventId ? "(optional — defaults to the event title)" : "*"}</label><input data-testid="invite-subject" className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={eventId ? "" : "You're invited to our reunion"} /></div>
            <div><label className="text-xs text-muted-foreground">Personal note</label><textarea data-testid="invite-message" rows={3} className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm" value={message} onChange={(e) => setMessage(e.target.value)} /></div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="invite-send" onClick={send} disabled={sending} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2"><Send className="h-4 w-4" /> {sending ? "Sending…" : `Send ${selected.size > 0 ? selected.size : ""} invitation${selected.size === 1 ? "" : "s"}`}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Page ---------------- */
export default function Faculty() {
  const [staff, setStaff] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("ud_faculty_view") || "grid");
  const [hideDeceased, setHideDeceased] = useState(() => localStorage.getItem("ud_faculty_hide_deceased") === "true");
  const [sortBy, setSortBy] = useUiState("ud_faculty_sort", "first");
  const [sortDir, setSortDir] = useUiState("ud_faculty_sortdir", "asc");
  const colClick = (col) => {
    const isName = col === "name";
    const active = isName ? (sortBy === "first" || sortBy === "last") : sortBy === col;
    if (active) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); return; }
    setSortBy(isName ? "last" : col);
    setSortDir("asc");
  };
  const [editing, setEditing] = useState(null); // staff object or "new"
  const [inviteOpen, setInviteOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [viewing, setViewing] = useState(null);
  useEffect(() => { localStorage.setItem("ud_faculty_view", view); }, [view]);
  useEffect(() => { localStorage.setItem("ud_faculty_hide_deceased", String(hideDeceased)); }, [hideDeceased]);
  const [searchParams, setSearchParams] = useSearchParams();
  const presetEvent = searchParams.get("invite") || "";
  const isAdmin = true; // route is Protected (staff); write endpoints enforce admin server-side

  useEffect(() => { if (presetEvent) setInviteOpen(true); }, [presetEvent]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (yearFilter) params.set("year_from", yearFilter);
    api.get(`/staff?${params.toString()}`).then((r) => setStaff(r.data.staff)).catch(() => setStaff([]));
  }, [q, status, yearFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data.events || [])).catch(() => {}); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try { await api.delete(`/staff/${s.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not remove"); }
  };

  const toggleSpotlight = async (s) => {
    try {
      const featured = !s.spotlight;
      await api.post(`/staff/${s.id}/spotlight`, { featured });
      toast.success(featured ? `${s.name} is now featured on the Experience home` : "Removed from the Experience home");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not update"); }
  };

  return (
    <div className="fade-up" data-testid="faculty-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
          <h1 className="font-serif text-3xl tracking-tight mt-1 flex items-center gap-2"><GraduationCap className="h-7 w-7 text-primary" /> Faculty &amp; Staff</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Professors and important staff and the years they served — so a class can invite everyone who was here during their era to a reunion or function.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="staff-invite-btn" onClick={() => setInviteOpen(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"><Send className="h-4 w-4" /> Invite to a function</button>
          <button data-testid="staff-add-btn" onClick={() => setEditing("new")} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"><Plus className="h-4 w-4" /> Add member</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="staff-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, title, department…" className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-background text-sm" />
        </div>
        <select data-testid="staff-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-md border border-border bg-background text-sm">
          <option value="">All statuses</option>
          {Object.keys(STATUS_LABELS).map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
        </select>
        <input data-testid="staff-year-filter" type="number" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} placeholder="Here in year…" className="w-36 px-3 py-2 rounded-md border border-border bg-background text-sm" />
        <button data-testid="staff-hide-deceased" onClick={() => setHideDeceased((v) => !v)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm ${hideDeceased ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>
          {hideDeceased ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Hide deceased
        </button>
        <div className="flex rounded-md border border-border overflow-hidden">
          <button data-testid="staff-view-grid" onClick={() => setView("grid")} title="Grid view" className={`p-2 ${view === "grid" ? "bg-primary text-white" : "hover:bg-muted"}`}><LayoutGrid className="h-4 w-4" /></button>
          <button data-testid="staff-view-list" onClick={() => setView("list")} title="List view" className={`p-2 ${view === "list" ? "bg-primary text-white" : "hover:bg-muted"}`}><List className="h-4 w-4" /></button>
        </div>
      </div>

      {(() => {
        const shown = sortFaculty((staff || []).filter((s) => !(hideDeceased && s.status === "deceased")), sortBy, sortDir);
        const available = new Set(shown.map((s) => letterOfFaculty(s.name, sortBy)).filter(Boolean));
        const jump = (L) => { const first = shown.find((s) => letterOfFaculty(s.name, sortBy) === L); if (first) document.getElementById(`fac-item-${first.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
        return (
      staff === null ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-16 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="staff-empty"><GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-40" /><p className="text-sm">No faculty or staff records match your filters.</p></div>
      ) : (
      <>
      <SortAlphaBar sortBy={sortBy} onSort={(v) => { setSortBy(v); setSortDir("asc"); }} available={available} onJump={jump} idPrefix="fac" />
      {view === "list" ? (
        <div className="bg-card border border-border rounded-lg divide-y divide-border overflow-hidden" data-testid="staff-list">
          <div className="flex items-center gap-3 px-4 py-2 bg-muted/50 text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="staff-list-header">
            <div className="h-9 w-9 shrink-0" aria-hidden />
            <button data-testid="colsort-name" onClick={() => colClick("name")} className={`min-w-0 flex-1 text-left flex items-center gap-1 hover:text-foreground transition-colors ${sortBy === "first" || sortBy === "last" ? "text-primary" : ""}`}>
              Name {(sortBy === "first" || sortBy === "last") && <SortArrow dir={sortDir} />}{sortBy === "first" && <span className="normal-case tracking-normal">(first)</span>}{sortBy === "last" && <span className="normal-case tracking-normal">(last)</span>}
            </button>
            <button data-testid="colsort-status" onClick={() => colClick("status")} className={`hidden sm:flex w-24 shrink-0 items-center gap-1 hover:text-foreground transition-colors ${sortBy === "status" ? "text-primary" : ""}`}>Status {sortBy === "status" && <SortArrow dir={sortDir} />}</button>
            <button data-testid="colsort-years" onClick={() => colClick("years")} className={`hidden sm:flex w-32 shrink-0 items-center gap-1 hover:text-foreground transition-colors ${sortBy === "years" ? "text-primary" : ""}`}>Years {sortBy === "years" && <SortArrow dir={sortDir} />}</button>
            <div className="hidden md:block w-56 shrink-0">Email</div>
            <div className="w-[92px] shrink-0 text-right">Actions</div>
          </div>
          {shown.map((s) => (
            <div key={s.id} id={`fac-item-${s.id}`} data-testid={`staff-row-${s.id}`} onClick={() => setViewing(s)} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/50 scroll-mt-24">
              <div className="h-9 w-9 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden border border-border shrink-0">
                {s.photo ? <img src={s.photo} alt={s.name} className="h-full w-full object-cover" loading="lazy" /> : <Initials name={s.name} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground truncate text-sm">{s.name}</div>
                <div className="text-xs text-muted-foreground truncate">{[s.title, s.department].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="hidden sm:block w-24 shrink-0">
                <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${STATUS_STYLES[s.status] || ""}`}>{STATUS_LABELS[s.status] || s.status}</span>
              </div>
              <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-32 shrink-0"><CalendarRange className="h-3.5 w-3.5 shrink-0" /> {tenure(s)}</div>
              <div className="hidden md:block text-xs text-muted-foreground w-56 truncate shrink-0">{s.email || "—"}</div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button data-testid={`staff-spotlight-${s.id}`} onClick={() => toggleSpotlight(s)} className={`p-1.5 rounded-md hover:bg-muted ${s.spotlight ? "text-accent" : "text-muted-foreground"}`} title={s.spotlight ? "Featured on Experience home" : "Feature on Experience home"}><Star className={`h-4 w-4 ${s.spotlight ? "fill-accent" : ""}`} /></button>
                <button data-testid={`staff-edit-${s.id}`} onClick={() => setEditing(s)} className="p-1.5 rounded-md hover:bg-muted" title="Edit"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`staff-delete-${s.id}`} onClick={() => remove(s)} className="p-1.5 rounded-md text-destructive hover:bg-destructive/10" title="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="staff-grid">
          {shown.map((s) => (
            <div key={s.id} id={`fac-item-${s.id}`} data-testid={`staff-card-${s.id}`} onClick={() => setViewing(s)} className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md hover:border-primary/40 transition-all scroll-mt-24">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden border border-border shrink-0">
                  {s.photo ? <img src={s.photo} alt={s.name} className="h-full w-full object-cover" loading="lazy" /> : <Initials name={s.name} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground truncate">{s.name}</div>
                  {s.title && <div className="text-xs text-muted-foreground truncate">{s.title}</div>}
                  {s.department && <div className="text-[11px] text-muted-foreground truncate">{s.department}</div>}
                </div>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded shrink-0 ${STATUS_STYLES[s.status] || ""}`}>{STATUS_LABELS[s.status] || s.status}</span>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <CalendarRange className="h-3.5 w-3.5" /> {tenure(s)}
                {s.email ? <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {s.email}</span> : <span className="flex items-center gap-1"><MailX className="h-3 w-3" /> no email</span>}
              </div>
              {s.bio && <p className="text-sm text-foreground/80 mt-2 line-clamp-2">{s.bio}</p>}
              {(s.wikipedia || s.linkedin || s.website || s.alumnus_id) && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`staff-links-${s.id}`} onClick={(e) => e.stopPropagation()}>
                  {s.alumnus_id && <Link to={`/alumni/${s.alumnus_id}`} data-testid={`staff-link-alumni-${s.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-accent/20 text-primary hover:bg-accent/30"><UserSquare2 className="h-3 w-3" /> Alumni page</Link>}
                  {s.wikipedia && <a href={s.wikipedia} target="_blank" rel="noreferrer" data-testid={`staff-link-wiki-${s.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-muted"><BookMarked className="h-3 w-3" /> Wikipedia</a>}
                  {s.linkedin && <a href={s.linkedin} target="_blank" rel="noreferrer" data-testid={`staff-link-linkedin-${s.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-muted"><Linkedin className="h-3 w-3" /> LinkedIn</a>}
                  {s.website && <a href={s.website} target="_blank" rel="noreferrer" data-testid={`staff-link-web-${s.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-muted"><Globe className="h-3 w-3" /> Website</a>}
                </div>
              )}
              <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-border" onClick={(e) => e.stopPropagation()}>
                <button data-testid={`staff-spotlight-${s.id}`} onClick={() => toggleSpotlight(s)} className={`p-1.5 rounded-md hover:bg-muted mr-auto ${s.spotlight ? "text-accent" : "text-muted-foreground"}`} title={s.spotlight ? "Featured on Experience home" : "Feature on Experience home"}><Star className={`h-4 w-4 ${s.spotlight ? "fill-accent" : ""}`} /></button>
                <button data-testid={`staff-edit-${s.id}`} onClick={() => setEditing(s)} className="p-1.5 rounded-md hover:bg-muted" title="Edit"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`staff-delete-${s.id}`} onClick={() => remove(s)} className="p-1.5 rounded-md text-destructive hover:bg-destructive/10" title="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )); })()}

      {editing && <StaffForm initial={editing === "new" ? null : editing} onSaved={load} onClose={() => setEditing(null)} />}
      {viewing && <StaffDetail s={viewing} onClose={() => setViewing(null)} onEdit={(m) => { setViewing(null); setEditing(m); }} />}
      {inviteOpen && <InviteModal allStaff={staff || []} events={events} presetEventId={presetEvent} onClose={() => { setInviteOpen(false); if (presetEvent) setSearchParams({}); }} />}
    </div>
  );
}
